import { styled } from "styled-components";
import { ActionButtonPropInterface } from "./contents";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery } from "@apollo/client";
import { CreateOrUpdateAdvanceBrandSettings } from "../../graphql/mutations/paymentlink";
import fetchGoogleFonts, { StatusEnum, sizes } from "./data";
import { RootState } from "../../store/rootReducer";
import { toast } from "react-toastify";
import { GET_BRAND_SETTINGS_BY_ORGANIZATION_ID } from "../../graphql/queries/paymentlink";
import { GET_USER_BY_ID } from "../../graphql/queries/userManagement";
import { LoadingSpinner, Overlay } from "../AddUserPage/AddUser";

interface BrandSettingsProps {
  ActionButtons: ActionButtonPropInterface;
}
export const BrandSettings: React.FC<BrandSettingsProps> = ({
  ActionButtons,
}) => {
  const [displayUploadSection, setDisplayUploadSection] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#ffffff");
  const [secondaryColor, setSecondaryColor] = useState("#ffffff");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [fontList, setFontList] = useState<string[]>([]);
  const organizationId: any = useSelector(
    (state: RootState) => state.user?.user.organizationId
  );
  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
  } = useQuery(GET_BRAND_SETTINGS_BY_ORGANIZATION_ID, {
    variables: { service: "payment-link", input: { organizationId } },
  });

  const [selectedHeadingFontStyle, setSelectedHeadingFontStyle] = useState(
    queryData?.getAdvanceBrandSettingsByOrganizationId?.Heading1FontStyle || ""
  );
  const handleFontStyleChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedHeadingFontStyle(event.target.value);
  };
  const [selectedHeadingFontSize, setSelectedHeadingFontSize] = useState(
    queryData?.getAdvanceBrandSettingsByOrganizationId?.Heading1FontSize || ""
  );
  const handleFontSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedHeadingFontSize(event.target.value);
  };

  const [selectedSubHeadingFontStyle, setSelectedSubHeadingFontStyle] =
    useState(
      queryData?.getAdvanceBrandSettingsByOrganizationId?.SubHeadingFontStyle ||
        ""
    );
  const handleSubHeadingFontStyleChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedSubHeadingFontStyle(event.target.value);
  };
  const [selectedSubHeadingFontSize, setSelectedSubHeadingFontSize] = useState(
    queryData?.getAdvanceBrandSettingsByOrganizationId?.SubHeadingFontSize || ""
  );
  const handleSubHeadingFontSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedSubHeadingFontSize(event.target.value);
  };

  const [selectedHeadingIIFontStyle, setSelectedHeadingIIFontStyle] = useState(
    queryData?.getAdvanceBrandSettingsByOrganizationId?.Heading2FontStyle || ""
  );
  const handleFontStyleIIChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedHeadingIIFontStyle(event.target.value);
  };
  const [selectedHeadingIIFontSize, setSelectedHeadingIIFontSize] = useState(
    queryData?.getAdvanceBrandSettingsByOrganizationId?.Heading2FontSize || ""
  );
  const handleFontSizeIIChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedHeadingIIFontSize(event.target.value);
  };

  useEffect(() => {
    const fetchFontList = async () => {
      try {
        const fonts = await fetchGoogleFonts();
        console.log("Fetched fonts:", fonts);
        setFontList(fonts);
      } catch (error) {
        console.error("Error fetching fonts:", error);
      }
    };
    fetchFontList();
  }, []);

  const [data, setData] = useState({
    primaryColor: "",
    secondaryColor: "",
    primaryLogo: "",
    whiteBackgroundLogo: "",
    Heading1fontStyle: "",
    Heading1fontSizes: "",
    Heading2fontStyle: "",
    Heading2fontSizes: "",
    SubHeadingfontStyle: "",
    SubHeadingfontSizes: "",
    companyName: "",
    companyDescription: "",
    keywords: "",
    socialMediaLinks: [] as string[],
    backgroundImages: [] as string[],
    headerImages: [] as string[],
    emailTemplates: "",
    customCss: "",
    meta: "",
    legalInformation: [] as string[],
    status: StatusEnum.ACTIVE,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLoaderClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const dataURL = reader.result as string;
        setSelectedImage(dataURL);
        setDisplayUploadSection(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const id = useSelector((state: RootState) => state.user?.user.id);

  const {
    loading: userLoading,
    error: userError,
    data: userData,
  } = useQuery(GET_USER_BY_ID, {
    variables: { getUserById: id },
  });

  const userDetails = userData?.getUserById;

  const [createOrUpdateBrandSettings, { loading }] = useMutation(
    CreateOrUpdateAdvanceBrandSettings,
    {
      variables: { service: "payment-link" },
    }
  );

  useEffect(() => {
    if (
      !queryLoading &&
      queryData &&
      queryData.getAdvanceBrandSettingsByOrganizationId
    ) {
      const {
        primaryColor: fetchedPrimaryColor,
        primaryLogo: fetchedPrimaryLogo,
        secondaryColor: fetchedSecondaryColor,
      } = queryData.getAdvanceBrandSettingsByOrganizationId;

      setPrimaryColor(fetchedPrimaryColor || "#ffffff");
      setSecondaryColor(fetchedSecondaryColor || "#ffffff");
      setSelectedImage(fetchedPrimaryLogo || null);
    }
  }, [queryLoading, queryData]);

  useEffect(() => {
    if (queryData && queryData.getAdvanceBrandSettingsByOrganizationId) {
      const existingData = queryData.getAdvanceBrandSettingsByOrganizationId;
      setData(existingData);
    }
  }, [queryData]);

  const handleReset = () => {
    setData({
      primaryColor: "",
      secondaryColor: "",
      primaryLogo: "",
      whiteBackgroundLogo: "",
      Heading1fontStyle: "",
      Heading1fontSizes: "",
      Heading2fontStyle: "",
      Heading2fontSizes: "",
      SubHeadingfontStyle: "",
      SubHeadingfontSizes: "",
      companyName: "",
      companyDescription: "",
      keywords: "",
      socialMediaLinks: [],
      backgroundImages: [],
      headerImages: [],
      emailTemplates: "",
      customCss: "",
      meta: "",
      legalInformation: [],
      status: StatusEnum.ACTIVE,
    });
    setIsDataChanged(false);
  };

  const handleSubmit = async () => {
    try {
      const inputData = {
        backgroundImages: data.backgroundImages,
        companyDescription: data.companyDescription,
        companyName: data.companyName,
        customCss: data.customCss,
        emailTemplates: data.emailTemplates,
        Heading1fontStyle: selectedHeadingFontStyle || data.Heading1fontStyle,
        Heading1fontSizes: selectedHeadingFontSize || data.Heading1fontSizes,
        Heading2fontStyle: selectedHeadingIIFontStyle || data.Heading2fontStyle,
        Heading2fontSizes: selectedHeadingIIFontSize || data.Heading2fontSizes,
        SubHeadingfontStyle:
          selectedSubHeadingFontStyle || data.SubHeadingfontStyle,
        SubHeadingfontSizes:
          selectedSubHeadingFontSize || data.SubHeadingfontSizes,
        keywords: data.keywords,
        headerImages: data.headerImages,
        legalInformation: data.legalInformation,
        meta: data.meta,
        primaryColor: primaryColor || data.primaryColor,
        primaryLogo: selectedImage || data.primaryLogo,
        secondaryColor: secondaryColor || data.secondaryColor,
        socialMediaLinks: data.socialMediaLinks,
        status: StatusEnum.ACTIVE,
        whiteBackgroundLogo: data.whiteBackgroundLogo,
      };

      if (queryData && queryData.getAdvanceBrandSettingsByOrganizationId) {
        await createOrUpdateBrandSettings({
          variables: {
            input: {
              ...inputData,
              id: queryData.getAdvanceBrandSettingsByOrganizationId.id,
            },
          },
        });
      } else {
        await createOrUpdateBrandSettings({
          variables: {
            input: {
              ...inputData,
              companyName: userDetails.organizationName,
            },
          },
        });
      }
      toast.success("Changes saved");
    } catch (error: any) {
      console.error("Error in saving brand settings:", error);
    }
  };

  const handlePrimaryColorChange = (event: any) => {
    const selectedPrimaryColor = event.target.value;
    setPrimaryColor(selectedPrimaryColor);
  };

  const handleSecondaryColorChange = (event: any) => {
    const selectedSecondaryColor = event.target.value;
    setSecondaryColor(selectedSecondaryColor);
  };

  return (
    <div>
      {" "}
      {queryLoading && (
        <Overlay>
          <LoadingSpinner />
        </Overlay>
      )}
      <SectionOne>
        <BodyOne>
          <p>Company Logo</p>
          <span>Upload your company's logo to customize your payment link</span>
        </BodyOne>
        <ContainerTwo>
          <BodyTwo>
            <Loader
              src="/assets/icons/ImageLoader.png"
              alt=""
              onClick={handleLoaderClick}
            />
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleUpload}
            />
            <div>
              <p>
                <span>click to replace</span> or drag and drop
                <p>SVG, PNG, JPG or GIF (max 800 X 400ppx)</p>
              </p>
            </div>
          </BodyTwo>

          <BodyThree>
            {displayUploadSection && (
              <img
                src={selectedImage || ""}
                alt="Uploaded Image"
                style={{ maxWidth: "100%", maxHeight: "100%" }}
              />
            )}
          </BodyThree>
        </ContainerTwo>
      </SectionOne>
      <SectionOne>
        <BodyOne>
          <p>Brand Color</p>
          <span>Upload your company's logo to customize your payment link</span>
        </BodyOne>
        <BodyFour>
          <SelectorSection>
            <InfoContainer>
              <p>Primary Color</p>{" "}
              <InfoCircle src="/assets/icons/info-circle.png" alt="" />
            </InfoContainer>
            <ColorSelector
              type="color"
              value={primaryColor}
              onChange={handlePrimaryColorChange}
            />
          </SelectorSection>

          <SelectorSection>
            <InfoContainer>
              <p>Secondary Color</p>{" "}
              <InfoCircle src="/assets/icons/info-circle.png" alt="" />
            </InfoContainer>
            <ColorSelector
              type="color"
              value={secondaryColor}
              onChange={handleSecondaryColorChange}
            />
          </SelectorSection>
        </BodyFour>
        <div></div>
      </SectionOne>
      <SectionTwo>
        <BodyOne>
          <p>Typography</p>
          <span>
            Select your preferred font(s) style and size for your brand
          </span>
        </BodyOne>
        <BodyFour>
          <FontContainer>
            <Option>
              <p>Heading</p>
            </Option>
            <FontContainer>
              <SelectorContainer>
                <p>Font Style</p>
                <FontSelector
                  value={selectedHeadingFontStyle}
                  onChange={handleFontStyleChange}
                >
                  <option value="">{data.Heading1fontStyle}</option>
                  {fontList.map((font) => (
                    <option key={font} value={font}>
                      {font}
                    </option>
                  ))}
                </FontSelector>
              </SelectorContainer>
              <SelectorContainer>
                <p>Font Size</p>
                <FontSelectorB
                  value={selectedHeadingFontSize}
                  onChange={handleFontSizeChange}
                >
                  <option value="">{data.Heading1fontSizes}</option>
                  {sizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </FontSelectorB>
              </SelectorContainer>
            </FontContainer>
          </FontContainer>
          <FontContainer>
            <Option>
              <p>Subheading</p>
            </Option>
            <FontContainer>
              <SelectorContainer>
                <p>Font Style</p>
                <FontSelector
                  value={selectedSubHeadingFontStyle}
                  onChange={handleSubHeadingFontStyleChange}
                >
                  <option value="">{data.SubHeadingfontStyle}</option>
                  {fontList.map((font) => (
                    <option key={font} value={font}>
                      {font}
                    </option>
                  ))}
                </FontSelector>
              </SelectorContainer>
              <SelectorContainer>
                <p>Font Size</p>
                <FontSelectorB
                  value={selectedSubHeadingFontSize}
                  onChange={handleSubHeadingFontSizeChange}
                >
                  <option value="">{data.SubHeadingfontSizes}</option>
                  {sizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </FontSelectorB>
              </SelectorContainer>
            </FontContainer>
          </FontContainer>
          <FontContainer>
            <Option>
              <p>Heading</p>
            </Option>
            <FontContainer>
              <SelectorContainer>
                <p>Font Style</p>
                <FontSelector
                  value={selectedHeadingIIFontStyle}
                  onChange={handleFontStyleIIChange}
                >
                  <option value="">{data.Heading2fontStyle}</option>
                  {fontList.map((font) => (
                    <option key={font} value={font}>
                      {font}
                    </option>
                  ))}
                </FontSelector>
              </SelectorContainer>
              <SelectorContainer>
                <p>Font Size</p>
                <FontSelectorB
                  value={selectedHeadingIIFontSize}
                  onChange={handleFontSizeIIChange}
                >
                  <option value="">{data.Heading2fontSizes}</option>
                  {sizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </FontSelectorB>
              </SelectorContainer>
            </FontContainer>
          </FontContainer>
        </BodyFour>
      </SectionTwo>
      <ButtonDiv>
        {ActionButtons(handleReset, handleSubmit, {
          values: data,
          isValid: true,
          isSubmitting: loading,
        })}
      </ButtonDiv>
    </div>
  );
};

const SectionOne = styled.section`
  display: flex;
  gap: 10rem;
  border-bottom: 1px solid #e8eaed;
  margin: 0;
  padding: 20px 0px;

  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 0;
    padding: 10px;
  }
`;

const SectionTwo = styled.section`
  display: flex;
  gap: 10rem;
  margin: 0;
  padding: 20px 0px;

  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 0;
    padding: 10px;
  }
`;

const BodyOne = styled.div`
  text-align: left;
  width: 16.875rem;
  p {
    margin: 0;
    padding: 0;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #0e0e0e;
  }

  span {
    margin: 0;
    padding: 0;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #868686;
  }

  @media screen and (max-width: 767px) {
    margin-bottom: 20px;
  }
`;

const BodyTwo = styled.div`
  width: 25.81rem;
  hehight: auto;
  padding: 15px 20px;
  display: flex;
  flex-direction: column;
  border: 1px solid #cfcfcf;
  border-style: dashed;
  border-radius: 10px;
  align-items: center;
  font-weight: 400;
  font-size: 14px;
  p span {
    font-weight: 500;
  }

  p {
    text-align: center;
    padding: 0;
    margin: 0;
  }
  @media screen and (max-width: 767px) {
    margin: 0;
  }
`;

const ContainerTwo = styled.div`
  display: flex;
  gap: 2rem;
  margin: 0;
  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 0;
    padding: 10px;
    align-items: center;
  }
`;

const Loader = styled.img`
  margin: 10px 0;
`;

const BodyThree = styled.div`
  padding: 0;
  margin: 0;
  width: 12.18rem;
  height: 10.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BodyFour = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;

  p {
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    margin: 0;
  }
`;

const InfoCircle = styled.img`
  width: 18px;
  height: 18px;
  margin-left: 5px;
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  margin: 10px 0;
`;

const FontContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-alignment: left;
  margin: 10px 0px;

  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 0;
    padding: 0px;
  }
`;

const FontSelector = styled.select`
  width: 22rem;
  padding: 8px 8px;
  border: 1px solid #e8eaed;
  border-radius: 12px;
  max-height: 200px;
`;

const FontSelectorB = styled.select`
  width: 8rem;
  padding: 8px 8px;
  border: 1px solid #e8eaed;
  border-radius: 12px;
`;

const Option = styled.div`
  margin-right: 20px;

  p {
    margin-top: 25px;
    font-weight: 400;
    font-size: 16px;
  }

  @media screen and (max-width: 767px) {
    margin-bottom: 20px;
  }
`;

const SelectorContainer = styled.div`
  margin-left: 10px;
  p {
    font-weight: 500;
    font-size: 14px;
  }
  @media screen and (max-width: 767px) {
    margin: 0;
    padding-bottom: 10px;
  }
`;

const SelectorSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media screen and (max-width: 767px) {
    margin: 0;
    padding-bottom: 5px;
  }
`;

const ButtonDiv = styled.div`
  padding-left: 1rem;
  @media screen and (max-width: 767px) {
    display: flex;
    justify-content: center;
  }
`;

const ColorSelector = styled.input`
  margin: 10px 0;
  width: 4rem;
  height: 1.5rem;
  margin-left: 2rem;
  border: none;
  border-radius: 5px;
`;
